import { Card, Tooltip, Rating, Paper, CardContent, CardHeader, Stack, Grid } from '@mui/material';
import React from 'react';
import { SecurityEyeIcon, CalendarTodayFilled } from 'component/commonComponents/Icon';
import { StyledConfigKBArticleCard } from './configKBArticleCard.style';
import { convertToShortNumber } from 'utils/functions';
import { handleKeyPress } from 'utils/functions';
import Typography from 'component/commonComponents/Typography';

export interface IConfigKBArticleCardProps {
  /**
   * Type of card
   */
  type: string;
  /**
   * kbNumber
   */
  kbNumber: string;
  /**
   * Title of card
   */
  title: string;
  /**
   * Description of card
   */
  description: string;
  /**
   * Rating
   */
  rate: number;
  /**
   * lastUpdated
   */
  lastUpdated: string;
  /**
   * viewCount
   */
  viewCount: number;
  /**
   * Optional click handler
   */
  onViewDetail: () => void;
  onHomePage?: boolean;
  /**
   * Convert number greater than 1000+ should be show style 1K
   */
  isConvertViewCountToShortNumber?: boolean;
  /**
   * Show Tooltip for View Count
   */
  isShowViewCountTooltip?: boolean;
  /**
   * Add spacing between short number
   */
  isShowViewCountShortNumberSpacing?: boolean;
  isShowingSummaryOnHomepageConfig?: boolean;
  locale?: {
    view: string;
    views: string;
    lastUpdated: string;
  };
}

const ConfigKBArticleCard = (props: IConfigKBArticleCardProps) => {
  let {
    title,
    rate,
    lastUpdated,
    viewCount,
    onViewDetail,
    isConvertViewCountToShortNumber,
    isShowViewCountTooltip,
    isShowViewCountShortNumberSpacing,
    locale,
    description,
  } = props;

  const clearSelection = () => {
    const selection = window.getSelection();
    if (selection) {
      selection.empty();
    }
  };

  return (
    <>
      <StyledConfigKBArticleCard
        onDragStart={clearSelection}
        tabIndex={0}
        onKeyDown={(e: any) => handleKeyPress(e, 'Enter', onViewDetail)}
      >
        <Card onClick={onViewDetail} className="uptime-accordion-details">
          <Paper>
            <CardContent sx={{ paddingBottom: '16px !important' }}>
              <CardHeader
                title={
                  <Tooltip title={title} placement="top-start">
                    <Typography kind="h6" className="title select-all-text" id="lbl-kbarticlecard-title">
                      {title}
                    </Typography>
                  </Tooltip>
                }
                sx={{ padding: 0, marginBottom: "14px" }}
              />
              <Tooltip title={description} placement="top-start">
                <Typography kind="caption" className="desc select-all-text" id="lbl-kbarticlecard-description">
                  {description}
                </Typography>
              </Tooltip>
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  marginTop: '12px',
                }}
              >
                <Grid alignItems="center" container item xs={12} md={6} gap={2}>
                  <Grid
                    container
                    item
                    xs={4}
                    alignItems="center"
                    justifyContent="flex-end"
                    role="img"
                    aria-label={`${locale?.lastUpdated} ${lastUpdated}`}
                  >
                    {lastUpdated && (
                      <>
                        <CalendarTodayFilled
                          className="icons"
                          alt="imageCard"
                          role="presentation"
                          aria-hidden="true"
                          focusable="false"
                        />
                        <Typography
                          kind="regular"
                          className="lastupdated"
                          id="lbl-kbarticlecard-last_updated"
                          role="presentation"
                        >
                          {lastUpdated}
                        </Typography>
                      </>
                    )}
                  </Grid>
                  <Grid container item xs={4} justifyContent="flex-end">
                    <Rating
                      className="rate"
                      name="size-small"
                      defaultValue={rate}
                      size="small"
                      readOnly
                      id="rtg-kbarticlecard-rating"
                      precision={0.5}
                    />
                  </Grid>
                  <Grid
                    container
                    item
                    xs={2.5}
                    alignItems="center"
                    justifyContent="flex-end"
                    role="img"
                    aria-label={`${viewCount} ${viewCount > 1 ? locale?.views : locale?.view}`}
                  >
                    <SecurityEyeIcon
                      className="icons"
                      alt="imageCard"
                      role="presentation"
                      aria-hidden="true"
                      focusable="false"
                    />
                    <Tooltip title={isShowViewCountTooltip ? viewCount : ''} placement="top-start">
                      <Typography
                        kind="regular"
                        className="viewcount"
                        id="lbl-kbarticlecard-view_count"
                        role="presentation"
                      >
                        {isConvertViewCountToShortNumber
                          ? convertToShortNumber(viewCount, { spacing: isShowViewCountShortNumberSpacing })
                          : viewCount}
                      </Typography>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Paper>
        </Card>
      </StyledConfigKBArticleCard>
    </>
  );
};

export default ConfigKBArticleCard;
